import { FC, useEffect, useState } from 'react';
import { UploadingOngoing } from '../commons/FileDataSourceConfigurationStatus';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { useWorkspace } from '@/features/workspaces/hooks';
import { updateBackgroundStatus } from '@/features/fileUploading/fileUploadingSlice';
import { EStatus } from '@/models/fileUpload';
import { ErrorStatus } from '@/components/inputs';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import { CongratulationScreen } from '@/global/components/CongratulationScreen';
import { DataSource, DataSourceFileConfig } from '@/models/dataSource';
import { StepPageActions } from '@/global/components';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';

type FileDataSourceConfigurationCompleted = {
  onInit?: VoidFunction;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
  isModal?: boolean;
};

export const FileDataSourceConfigurationCompletedStep: FC<FileDataSourceConfigurationCompleted> = ({ onInit, onNextStep, onPrevStep, onCancel, isModal }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;
  const dataSourceType = dataSource.type;
  const fileUploadStatus = useAppSelector((state) => state.fileUploading.status);
  const fileUploadData = useAppSelector((state) => state.fileUploading.fileInfo);

  const [saveDataSource, { data: savedDataSource }] = useSaveDataSourceMutation();
  const [continueInBackground, setContinueInBackground] = useState(false);

  const handleContinueInBackground = () => {
    dispatch(updateBackgroundStatus(workspace.id));
    setContinueInBackground(true);
  };

  const completedStep = async () => {
    if (dataSource && dataSource.name && dataSource.configuration) {
      await saveDataSource({
        workspaceId: workspace.id,
        dataSource: {
          type: dataSourceType.id,
          name: dataSource.name,
          configuration: {
            isDefaultHeader: dataSource.configuration?.isDefaultHeader,
            headers: dataSource.configuration?.headers,
            type: dataSource.configuration?.type,
            url: dataSource.configuration?.url || fileUploadStatus?.data?.path,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (savedDataSource) {
      dispatch(stashDataSource(savedDataSource.dataSource));
    }
  }, [savedDataSource]);

  useEffect(() => {
    completedStep();
    onInit();
  }, []);

  if (dataSource?.configuration) {
    if (continueInBackground || fileUploadStatus?.uploadingStatus === EStatus.SUCCESS) {
      return (
        <CongratulationScreen
          title='data_source_edit:code.installation_success.data_source_created_succesfully'
          subtitle='data_source_edit:code.installation_success.data_source_created_succesfully_subtitle'
          primaryButton={
            isModal
              ? {
                  text: 'data_source_edit:code.installation_success.choose_destination',
                  onClick: onNextStep,
                }
              : {
                  text: 'data_source_edit:code.installation_success.create_new_campaing',
                  onClick: onNextStep,
                }
          }
          secondaryButton={
            isModal
              ? {
                  text: 'data_source_edit:code.installation_success.add_another_data_source',
                  onClick: onPrevStep,
                }
              : {
                  text: 'data_source_edit:code.installation_success.view_all_data_sources',
                  onClick: onPrevStep,
                }
          }
        />
      );
    } else if (fileUploadStatus?.uploadingStatus === EStatus.UPLOADING) {
      return (
        <UploadingOngoing
          fileUpload={{
            status: fileUploadStatus.uploadingStatus,
            percentage: fileUploadStatus.progress || { total: 0, loaded: 0, percentageCompleted: 0 },
          }}
          filename={fileUploadData?.filename || ''}
          onClick={handleContinueInBackground}
        />
      );
    } else if (fileUploadStatus?.uploadingStatus === EStatus.ERROR) {
      return (
        <>
          <ErrorStatus error={fileUploadStatus.error} />;
          <StepPageActions
            primaryButton={{
              text: 'COMMON:cancel',
              onClick: onCancel,
            }}
          />
        </>
      );
    } else {
      return null;
    }
  }
  return null;
};
